import { toast } from '@/components/ui/Toast/Toast';
import { useBookAppointment } from '@/features/scheduling/api/bookAppointment';
import { GET_CALENDAR_PERIOD_QUERY_KEY } from '@/features/scheduling/api/getCalendarPeriod';
import { GET_TIMESLOTS_PER_DAY_QUERY_KEY } from '@/features/scheduling/api/getTimeslotsPerDay';
import { getMaxCalendarDate } from '@/features/scheduling/utils/getMaxCalendarDate';
import { getRecurrenceEndDateOptions, getRecurrenceEndDatePresetKeys } from '@/features/scheduling/utils/recurrenceEndDateOptions';
import { useTimeFor } from '@/hooks/useTimeFor';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import type { RecurrencePattern } from '@/types/scheduling';
import { centerDayFromKey, toUtcIsoString } from '@/utils/timezone';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIsFetching } from '@tanstack/react-query';
import { endOfDay, format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useScheduleFlow } from '../../context/useScheduleFlow';

export const useDateAndTimeStep = () => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'scheduleDialog.steps.dateAndTimeStep' });
  const { t: tUtils } = useTranslation('utils');
  const { t: tInterval } = useTranslation('scheduling', { keyPrefix: 'interval' });
  const { translateError } = useErrorTranslation();

  const { timeZone: centerTimeZone, zoned, now } = useTimeFor('sessionTime');

  const { data, goToNextStep, goToPreviousStep, updateData, goToStep, setErrorType } = useScheduleFlow();

  const formSchema = z
    .object({
      dateAndTime: z.string(),
      recurringEnabled: z.boolean(),
      recurrencePattern: z.string().optional(),
      endDate: z.string().optional(),
      customEndDate: z.string().optional(),
    })
    .refine(
      (data) => {
        return data.dateAndTime && data.dateAndTime.length > 0;
      },
      {
        message: tUtils('validation.noDateSelected'),
        path: ['dateAndTime'],
      },
    )
    .refine(
      (data) => {
        if (data.recurringEnabled) {
          return data.recurrencePattern && data.recurrencePattern.length > 0;
        }
        return true;
      },
      {
        message: tUtils('validation.required'),
        path: ['recurrencePattern'],
      },
    )
    .refine(
      (data) => {
        if (data.recurringEnabled) {
          return data.endDate && data.endDate.length > 0;
        }
        return true;
      },
      {
        message: tUtils('validation.required'),
        path: ['endDate'],
      },
    )
    .refine(
      (data) => {
        if (data.recurringEnabled && data.endDate === 'custom') {
          return data.customEndDate && data.customEndDate.length > 0;
        }
        return true;
      },
      {
        message: tUtils('validation.noDateSelected'),
        path: ['customEndDate'],
      },
    );

  const presetEndDateKeys = getRecurrenceEndDatePresetKeys(now(), centerTimeZone);
  const isPresetEndDate = (dayKey: string) => presetEndDateKeys.includes(dayKey);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      dateAndTime: data.dateAndTime || '',
      recurringEnabled: data.recurring.enabled,
      recurrencePattern: data.recurring.recurrencePattern ?? '',
      endDate: (() => {
        const storedEndDate = data.recurring.endDate;
        if (!storedEndDate) return '';

        return isPresetEndDate(storedEndDate) ? storedEndDate : 'custom';
      })(),
      customEndDate: (() => {
        const storedEndDate = data.recurring.endDate;
        if (!storedEndDate) return '';

        return isPresetEndDate(storedEndDate) ? '' : storedEndDate;
      })(),
    },
  });

  const recurringEnabled = form.watch('recurringEnabled');
  const selectedDateTime = form.watch('dateAndTime');

  const selectedWeekday = (() => {
    if (!selectedDateTime) return '';
    try {
      return format(zoned(selectedDateTime), 'EEEE') + 's';
    } catch {
      return '';
    }
  })();

  const getRepeatOptions = (): { value: RecurrencePattern; label: string }[] => {
    if (!selectedWeekday) {
      return [
        { value: 'weekly', label: tInterval('weekly') },
        { value: 'biweekly', label: tInterval('biweekly') },
        { value: 'fourWeekly', label: tInterval('fourWeekly') },
      ];
    }

    return [
      { value: 'weekly', label: `${tInterval('weekly')} ${tInterval('on')} ${selectedWeekday}` },
      { value: 'biweekly', label: `${tInterval('biweekly')} ${tInterval('on')} ${selectedWeekday}` },
      { value: 'fourWeekly', label: `${tInterval('fourWeekly')} ${tInterval('on')} ${selectedWeekday}` },
    ];
  };
  const repeatOptions = getRepeatOptions();

  const maxRecurringDates =
    data.studentSessionDetails?.map((student) =>
      getMaxCalendarDate(student.enrollmentEndDate, student.centerDetails.max_advance_booking_recurring, centerTimeZone),
    ) ?? [];

  const maxRecurringDate: Date | undefined = maxRecurringDates.length > 0 ? zoned(Math.min(...maxRecurringDates.map((d) => d.getTime()))) : undefined;

  const endDateOptions = getRecurrenceEndDateOptions({
    today: now(),
    timeZone: centerTimeZone,
    firstSession: zoned(selectedDateTime),
    maxRecurringDate,
  }).map(({ value, labelKey }) => ({ value, label: t(labelKey) }));

  const selectedEndDate = form.watch('endDate');
  const isCustomEndDate = selectedEndDate === 'custom';

  const { mutate: bookAppointment, isPending: isBookingLoading } = useBookAppointment({
    options: {
      onSuccess: (data, payload) => {
        updateData('bookedAppointmentsDetails', data);

        const isPlan = payload.isPlan ?? false;

        if (!isPlan && (!data?.bookedAppointments || data?.bookedAppointments?.length === 0)) {
          setErrorType('fullyBooked');
          goToStep('error');
          return;
        }
        goToNextStep();
      },
      onError: (error) => {
        if (error.errors?.some((e) => e.code === 'TIMESLOT_NOT_AVAILABLE')) {
          setErrorType('fullyBooked');
          goToStep('error');
        } else {
          toast({
            variant: 'destructive',
            description: translateError(error),
          });
        }
      },
    },
  });

  const isTimeslotsFetching = useIsFetching({
    queryKey: [GET_TIMESLOTS_PER_DAY_QUERY_KEY],
  });

  const isCalendarPeriodFetching = useIsFetching({
    queryKey: [GET_CALENDAR_PERIOD_QUERY_KEY],
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    if (isTimeslotsFetching) {
      toast({
        variant: 'destructive',
        description: tUtils('validation.noTimeSelected'),
      });
      return;
    }

    if (!values.dateAndTime) {
      toast({
        variant: 'destructive',
        description: tUtils('validation.noDateSelected'),
      });
      return;
    }

    void captureAnalyticsEvent('scheduling_date_selected', {
      dateAndTime: values.dateAndTime,
      recurringEnabled: values.recurringEnabled,
      recurrencePattern: values.recurrencePattern,
      endDate: values.endDate === 'custom' ? values.customEndDate : values.endDate,
      studentIds: data.studentSessionDetails?.map((s) => s.studentId) ?? [],
    });
    updateData('dateAndTime', values.dateAndTime || '');
    updateData('recurring', {
      enabled: values.recurringEnabled,
      recurrencePattern: values.recurrencePattern as RecurrencePattern | undefined,
      endDate: values.endDate === 'custom' ? values.customEndDate : values.endDate,
    });

    bookAppointment({
      students: data.studentSessionDetails!.map((student) => ({
        studentId: student.studentId,
        centerId: student.centerDetails.center_id,
        enrollmentId: student.enrollmentId,
        sessionType: student.sessionType,
        deliveryType: student.deliveryType,
        duration: student.durationInMinutes,
      })),
      isPlan: true,
      selectedTimeslot: new Date(values.dateAndTime).toISOString(),
      ...(values.recurringEnabled && {
        recurringSettings: {
          recurrencePattern: values.recurrencePattern as RecurrencePattern,
          endDate: toUtcIsoString(endOfDay(centerDayFromKey(values.endDate === 'custom' ? values.customEndDate! : values.endDate!, centerTimeZone))),
        },
      }),
    });
  };

  const studentsConfig = data?.config?.data?.students ?? [];
  const isSingleStudent = studentsConfig.length <= 1;
  const hasSettings = data?.hasSessionDetailsSettings;

  const goToPrevStep = () => {
    if (hasSettings) {
      return goToPreviousStep();
    }

    if (!isSingleStudent && !hasSettings) {
      return goToStep('selectStudents');
    }
  };

  return {
    form,
    onSubmit,
    data,
    recurringEnabled,
    repeatOptions,
    endDateOptions,
    isCustomEndDate,
    goToPrevStep,
    maxRecurringDate,
    isBookingLoading,
    isContinueDisabled: isCalendarPeriodFetching > 0 || isTimeslotsFetching > 0 || !selectedDateTime,
    canGoBack: !isSingleStudent || hasSettings,
  } as const;
};
